import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchCandidates } from './apiService';

// Updated Custom Hook for Form Validation - Now supports both modes
export const useFormValidation = (formData, selectedFiles, processingMode, jobDescriptionData) => {
    const [errors, setErrors] = useState({});

    const validateForm = () => {
        const newErrors = {};

        // Always validate files
        if (!selectedFiles || selectedFiles.length === 0) {
            newErrors.files = 'Please upload at least one resume file';
        }

        if (processingMode === 'job_description') {
            // Job Description mode validation
            if (!jobDescriptionData?.jobDescription?.trim()) {
                newErrors.jobDescription = 'Job description is required';
            } else if (jobDescriptionData.jobDescription.trim().length < 50) {
                newErrors.jobDescription = 'Job description must be at least 50 characters long';
            } else if (jobDescriptionData.jobDescription.trim().length > 10000) {
                newErrors.jobDescription = 'Job description is too long (maximum 10,000 characters)';
            }
        } else {
            // Manual mode validation (existing logic)
            if (!formData.jobTitle?.trim()) {
                newErrors.jobTitle = 'Job title is required';
            }
            
            if (!formData.experience?.trim()) {
                newErrors.experience = 'Experience requirement is required';
            }
            
            if (!formData.requiredSkills?.trim()) {
                newErrors.requiredSkills = 'Required skills are required';
            } else {
                // Validate skills format
                const skills = formData.requiredSkills.split(',').map(s => s.trim()).filter(s => s.length > 0);
                if (skills.length === 0) {
                    newErrors.requiredSkills = 'At least one skill is required';
                } else if (skills.length > 20) {
                    newErrors.requiredSkills = 'Too many skills (maximum 20)';
                }
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    return { errors, validateForm, setErrors };
};

// Enhanced Custom Hook for Data Polling with correct API data structure
export const useDataPolling = (expectedCount, startTime, isEnabled) => {
    const queryClient = useQueryClient();
    
    const { 
        data: candidates, 
        isSuccess, 
        isError, 
        error,
        failureCount 
    } = useQuery({
        queryKey: ['candidates', 'polling', startTime], // Include startTime in key for better caching
        queryFn: async () => {
            // console.log('🔄 Polling for candidates...', { 
            //     expectedCount, 
            //     startTime: startTime ? new Date(startTime).toISOString() : 'N/A',
            //     isEnabled 
            // });
            
            try {
                const result = await fetchCandidates();
                // console.log('📊 Polling result:', { 
                //     candidatesFound: result?.length || 0,
                //     expectedCount,
                //     timestamp: new Date().toISOString(),
                //     rawResult: result
                // });
                return result;
            } catch (error) {
                console.error('❌ Polling error:', error);
                throw error;
            }
        },
        enabled: isEnabled && !!startTime && expectedCount > 0,
        refetchInterval: (data, query) => {
            // console.log('⏱️ Refetch interval check:', {
            //     hasData: !!data,
            //     dataLength: data?.length || 0,
            //     hasError: !!query.state.error,
            //     isEnabled,
            //     expectedCount
            // });
            
            // Don't refetch if we have errors or if polling is disabled
            if (query.state.error || !isEnabled) {
                //console.log('🛑 Stopping refetch due to error or disabled polling');
                return false;
            }
            
            // Check if we've found all candidates based on your actual API structure
            if (data && Array.isArray(data) && expectedCount > 0) {
                // Count new candidates since startTime
                const newCandidatesCount = countNewCandidates(data, startTime);
                
                // console.log('🎯 Polling completion check:', {
                //     newCandidatesFound: newCandidatesCount,
                //     expectedCount,
                //     isComplete: newCandidatesCount >= expectedCount
                // });
                
                // Stop polling if we have all candidates
                if (newCandidatesCount >= expectedCount) {
                    //console.log('✅ All candidates found, stopping polling');
                    return false;
                }
            }
            
            return 3000; // Continue polling every 3 seconds
        },
        refetchIntervalInBackground: true,
        staleTime: 0, // Always consider data stale to ensure fresh polling
        retry: (failureCount, error) => {
            // console.log(`🔄 Polling retry attempt ${failureCount}:`, error?.message);
            
            // Don't retry if it's an auth error
            if (error?.message?.includes('Authentication failed') || 
                error?.message?.includes('Authentication token missing')) {
                // console.log('🔒 Authentication error during polling - stopping retries');
                return false;
            }
            
            // Stop retrying after 3 attempts
            if (failureCount >= 3) {
                // console.log('❌ Max polling retries reached');
                return false;
            }
            
            return true;
        },
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000), // Exponential backoff
    });

    // Helper function to count new candidates based on your API structure
    const countNewCandidates = (candidatesData, processingStartTime) => {
        if (!candidatesData || !Array.isArray(candidatesData) || !processingStartTime) {
            return 0;
        }
        
        try {
            const startDateTime = new Date(processingStartTime);
            
            // Since your API doesn't return appliedDate, we'll use application_id as a proxy
            // Higher application_id numbers likely indicate more recent applications
            // Or check if the candidate was created after the processing start time
            
            // Method 1: Count all candidates (assuming they're all new if processing just started)
            if (candidatesData.length > 0) {
                // console.log('📊 Counting candidates:', {
                //     totalCandidates: candidatesData.length,
                //     sampleCandidate: candidatesData[0],
                //     startTime: startDateTime.toISOString()
                // });
                
                // If your API includes a timestamp field, use that
                // Otherwise, assume all returned candidates are new for this processing session
                return candidatesData.length;
            }
            
            return 0;
        } catch (err) {
            console.error('❌ Error counting new candidates:', err);
            return 0;
        }
    };

    const newCandidatesCount = React.useMemo(() => {
        return countNewCandidates(candidates, startTime);
    }, [candidates, startTime]);

    // Enhanced completion detection
    const isComplete = React.useMemo(() => {
        const complete = expectedCount > 0 && 
                        isEnabled && 
                        newCandidatesCount >= expectedCount && 
                        isSuccess &&
                        !isError;
        
        if (complete) {
            console.log('🎉 Processing marked as complete:', {
                newCandidatesCount,
                expectedCount,
                isEnabled,
                isSuccess,
                isError,
                candidatesData: candidates?.slice(0, 2) // Show first 2 candidates for debugging
            });
        }
        
        return complete;
    }, [expectedCount, isEnabled, newCandidatesCount, isSuccess, isError, candidates]);

    // Enhanced error detection
    const pollingError = React.useMemo(() => {
        if (!isError) return null;
        
        console.error('📡 Polling error detected:', {
            error: error?.message,
            failureCount,
            isEnabled
        });
        
        // Check for authentication errors specifically
        if (error?.message?.includes('Authentication failed') || 
            error?.message?.includes('Authentication token missing')) {
            return error;
        }
        
        // Return error after multiple failures
        if (failureCount >= 3) {
            return error;
        }
        
        return null;
    }, [isError, error, failureCount]);

    // Stop polling when complete or on authentication error
    React.useEffect(() => {
        if (isComplete && isEnabled) {
            // console.log('✅ Polling complete - invalidating queries');
            queryClient.setQueryData(['candidates', 'polling', startTime], candidates);
            // Invalidate main candidates query to refresh dashboard
            queryClient.invalidateQueries(['candidates']);
        }
    }, [isComplete, isEnabled, candidates, queryClient, startTime]);

    // Enhanced logging for debugging
    React.useEffect(() => {
        if (isEnabled) {
            console.log('📊 Polling status update:', {
                newCandidatesCount,
                expectedCount,
                isComplete,
                isError,
                errorMessage: error?.message,
                candidatesReceived: candidates?.length || 0,
                isPollingActive: isEnabled && !isComplete && !pollingError,
                timestamp: new Date().toISOString()
            });
        }
    }, [newCandidatesCount, expectedCount, isComplete, isError, error, isEnabled, candidates, pollingError]);

    return {
        newCandidatesCount,
        isComplete,
        totalCandidates: candidates?.length || 0,
        pollingError,
        isPolling: isEnabled && !isComplete && !pollingError,
        candidates, // Include raw candidates data for debugging
        candidatesData: candidates // Additional reference to the data
    };
};

// Custom Hook for Authentication (unchanged)
export const useAuthentication = (navigate) => {
    const [authError, setAuthError] = useState('');
    const [isCheckingAuth, setIsCheckingAuth] = useState(true);

    const checkAuth = () => {
        //console.log('🔐 Checking authentication for resume upload...');
        
        const adminToken = localStorage.getItem('adminToken');
        const adminUser = localStorage.getItem('adminUser');

        if (!adminToken || !adminUser) {
            //console.log('🔒 No authentication found, redirecting to login...');
            setAuthError('No authentication found');
            navigate('/login', { replace: true });
            return false;
        }

        if (!adminToken.startsWith('admin_session_')) {
            //console.log('🔒 Invalid token format, redirecting to login...');
            setAuthError('Invalid token format');
            localStorage.removeItem('adminToken');
            localStorage.removeItem('adminUser');
            navigate('/login', { replace: true });
            return false;
        }

        try {
            const base64 = adminToken.replace('admin_session_', '');
            const decoded = atob(base64);
            const tokenData = JSON.parse(decoded);

            const now = Math.floor(Date.now() / 1000);
            if (tokenData.exp < now) {
                //console.log('🔒 Token expired, redirecting to login...');
                setAuthError('Token expired');
                localStorage.removeItem('adminToken');
                localStorage.removeItem('adminUser');
                navigate('/login', { replace: true });
                return false;
            }

            //console.log('✅ Authentication check passed');
            setAuthError('');
            return true;
        } catch (err) {
           // console.log('🔒 Token validation failed, redirecting to login...');
            setAuthError('Token validation failed');
            localStorage.removeItem('adminToken');
            localStorage.removeItem('adminUser');
            navigate('/login', { replace: true });
            return false;
        }
    };

    return {
        authError,
        setAuthError,
        isCheckingAuth,
        setIsCheckingAuth,
        checkAuth
    };
};